import React, { useContext, useEffect, useMemo, useState } from 'react';
import AppHeader from '../components/AppHeader';
import { AuthContext } from '../context/AuthContext';
import { dealerAvailabilityAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';

const weekdays = [
  { label: 'Sun', value: 0 },
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
];

const DealerSlotsScreen = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [error, setError] = useState('');

  // Rules state
  const [rules, setRules] = useState([]); // [{weekday,startTime,endTime,intervalMinutes,capacity}]
  const [savingRules, setSavingRules] = useState(false);

  // Generate state
  const [genFrom, setGenFrom] = useState('');
  const [genTo, setGenTo] = useState('');
  const [generating, setGenerating] = useState(false);

  // Clear state
  const [clearFrom, setClearFrom] = useState('');
  const [clearTo, setClearTo] = useState('');
  const [onlyEmpty, setOnlyEmpty] = useState(true);
  const [clearing, setClearing] = useState(false);

  // Slots state
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  // On mount: role guard - only dealer_admin and admin can manage slots
  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'dealer_admin' && user.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  // Fetch rules
  const fetchRules = async () => {
    try {
      const { data } = await dealerAvailabilityAPI.rulesGet();
      setRules(data || []);
    } catch (e) {
      console.error('Fetch rules error', e);
    }
  };

  // Fetch slots
  const fetchSlots = async () => {
    try {
      setLoadingSlots(true);
      setError('');
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const { data } = await dealerAvailabilityAPI.list(params);
      setSlots(data || []);
    } catch (e) {
      console.error('Fetch slots error', e);
      setError(
        e.response?.data?.message ||
          e.message ||
          'Failed to load availability slots.'
      );
    } finally {
      setLoadingSlots(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (user && (user.role === 'dealer_admin' || user.role === 'admin')) {
      fetchRules();
      fetchSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Update one rule entry
  const updateRule = (idx, patch) => {
    setRules((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  // Add a new rule row
  const addRule = () => {
    setRules((prev) => [
      ...prev,
      {
        weekday: 1, // Monday by default
        startTime: '09:00',
        endTime: '17:00',
        intervalMinutes: 60,
        capacity: 1,
      },
    ]);
  };

  const removeRule = (idx) => {
    setRules((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveRules = async () => {
    try {
      setSavingRules(true);
      setError('');
      await dealerAvailabilityAPI.rulesSave({ rules });
    } catch (e) {
      console.error('Save rules error', e);
      setError(
        e.response?.data?.message || e.message || 'Failed to save rules.'
      );
    } finally {
      setSavingRules(false);
    }
  };

  const generate = async () => {
    if (!genFrom || !genTo) {
      setError('Select a From and To date range to generate slots.');
      return;
    }
    try {
      setGenerating(true);
      setError('');
      await dealerAvailabilityAPI.generate({
        from: genFrom,
        to: genTo,
        useRules: true,
      });
      await fetchSlots();
    } catch (e) {
      console.error('Generate slots error', e);
      setError(
        e.response?.data?.message || e.message || 'Failed to generate slots.'
      );
    } finally {
      setGenerating(false);
    }
  };

  const clearRange = async () => {
    if (!clearFrom || !clearTo) {
      setError('Select a From and To date range to clear slots.');
      return;
    }
    try {
      setClearing(true);
      setError('');
      await dealerAvailabilityAPI.clearRange({
        from: clearFrom,
        to: clearTo,
        onlyEmpty,
      });
      await fetchSlots();
    } catch (e) {
      console.error('Clear slots error', e);
      setError(
        e.response?.data?.message || e.message || 'Failed to clear slots.'
      );
    } finally {
      setClearing(false);
    }
  };

  const removeSlot = async (slotId) => {
    try {
      setError('');
      await dealerAvailabilityAPI.remove(slotId);
      await fetchSlots();
    } catch (e) {
      console.error('Remove slot error', e);
      setError(
        e.response?.data?.message || e.message || 'Failed to remove slot.'
      );
    }
  };

  // Group slots by date for calendar-ish display
  const groupedSlots = useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      const key = new Date(s.date).toDateString();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(s);
    }
    // sort slots within each day
    map.forEach((arr) => {
      arr.sort((a, b) => {
        const am =
          parseInt(a.time.slice(0, 2), 10) * 60 +
          parseInt(a.time.slice(3, 5), 10);
        const bm =
          parseInt(b.time.slice(0, 2), 10) * 60 +
          parseInt(b.time.slice(3, 5), 10);
        return am - bm;
      });
    });
    // sort days chronologically
    return Array.from(map.entries()).sort(
      ([dayA], [dayB]) => new Date(dayA) - new Date(dayB)
    );
  }, [slots]);

  // Quick range helpers
  const setNextDays = (days) => {
    const today = new Date();
    const fromDate = new Date(today);
    const toDate = new Date(today);
    toDate.setDate(today.getDate() + days);

    const toIso = (d) => d.toISOString().slice(0, 10);
    setFrom(toIso(fromDate));
    setTo(toIso(toDate));
  };

  return (
    <div className="flex flex-col h-full">
      <AppHeader
        title="Slot Management"
        subtitle="Control your weekly hours and available time slots"
      />

      <div className="flex-1 overflow-y-auto p-4 pb-20 space-y-4 text-xs">
        {error && (
          <div className="bg-red-900/30 text-red-300 px-3 py-2 rounded-xl">
            {error}
          </div>
        )}

        {/* Rules + Generate card */}
        <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-3 border border-app-border-light dark:border-app-border space-y-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div>
              <div className="font-semibold text-sm text-app-text-light dark:text-app-text">
                Weekly Rules
              </div>
              <div className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary">
                Define your standard hours by weekday. These rules are used to
                generate available slots over any date range.
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={addRule}
                className="px-3 py-2 rounded-xl bg-app-text-light dark:bg-app-text text-app-bg-light dark:text-app-bg font-semibold"
              >
                + Add rule
              </button>
              <button
                type="button"
                disabled={savingRules}
                onClick={saveRules}
                className={`px-3 py-2 rounded-xl font-semibold ${
                  savingRules
                    ? 'bg-app-bg-light dark:bg-app-bg text-app-text-secondary-light dark:text-app-text-secondary'
                    : 'bg-app-accent text-black'
                }`}
              >
                {savingRules ? 'Saving…' : 'Save rules'}
              </button>
            </div>
          </div>

          {/* Rules grid */}
          {rules.length === 0 ? (
            <div className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary">
              No weekly rules yet. Add at least one rule to start generating
              slots (e.g., Mon–Fri, 9am–5pm every 60 minutes).
            </div>
          ) : (
            <div className="space-y-2">
              {rules.map((r, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.1fr)_repeat(4,minmax(0,1fr))] gap-2 bg-app-bg-light dark:bg-app-bg border border-app-border-light dark:border-app-border rounded-xl p-2"
                >
                  {/* Weekday */}
                  <div>
                    <label className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">Weekday</label>
                    <select
                      className="mt-1 w-full px-2 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border"
                      value={r.weekday}
                      onChange={(e) =>
                        updateRule(idx, { weekday: Number(e.target.value) })
                      }
                    >
                      {weekdays.map((w) => (
                        <option key={w.value} value={w.value}>
                          {w.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Start */}
                  <div>
                    <label className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">Start</label>
                    <input
                      type="time"
                      className="mt-1 w-full px-2 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border"
                      value={r.startTime}
                      onChange={(e) =>
                        updateRule(idx, { startTime: e.target.value })
                      }
                    />
                  </div>

                  {/* End */}
                  <div>
                    <label className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">End</label>
                    <input
                      type="time"
                      className="mt-1 w-full px-2 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border"
                      value={r.endTime}
                      onChange={(e) =>
                        updateRule(idx, { endTime: e.target.value })
                      }
                    />
                  </div>

                  {/* Interval */}
                  <div>
                    <label className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">
                      Interval (min)
                    </label>
                    <input
                      type="number"
                      min="5"
                      className="mt-1 w-full px-2 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border"
                      value={r.intervalMinutes}
                      onChange={(e) =>
                        updateRule(idx, {
                          intervalMinutes: Number(e.target.value || 60),
                        })
                      }
                    />
                  </div>

                  {/* Capacity + Remove */}
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">
                        Capacity
                      </label>
                      <input
                        type="number"
                        min="1"
                        className="mt-1 w-full px-2 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border"
                        value={r.capacity}
                        onChange={(e) =>
                          updateRule(idx, {
                            capacity: Number(e.target.value || 1),
                          })
                        }
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRule(idx)}
                      className="self-end mb-0.5 px-3 py-2 rounded-lg bg-red-600/80 text-black text-[11px]"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Generate from rules */}
          <div className="mt-3 border-t border-app-border-light dark:border-app-border pt-3 space-y-2">
            <div className="text-sm font-semibold text-app-text-light dark:text-app-text">
              Generate slots from rules
            </div>
            <div className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary">
              Pick a date range and we will create time slots based on your
              weekly rules. Existing slots won&apos;t be duplicated.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
              <div>
                <label className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">From</label>
                <input
                  type="date"
                  className="mt-1 w-full px-2 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border"
                  value={genFrom}
                  onChange={(e) => setGenFrom(e.target.value)}
                />
              </div>
              <div>
                <label className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">To</label>
                <input
                  type="date"
                  className="mt-1 w-full px-2 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border"
                  value={genTo}
                  onChange={(e) => setGenTo(e.target.value)}
                />
              </div>
              <button
                type="button"
                disabled={generating}
                onClick={generate}
                className={`w-full sm:w-auto px-3 py-2 rounded-xl font-semibold ${
                  generating
                    ? 'bg-app-bg-light dark:bg-app-bg text-app-text-secondary-light dark:text-app-text-secondary'
                    : 'bg-app-accent text-black'
                }`}
              >
                {generating ? 'Generating…' : 'Generate slots'}
              </button>
            </div>
          </div>
        </div>

        {/* Calendar-style Slots viewer */}
        <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl p-3 border border-app-border-light dark:border-app-border space-y-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-app-text-light dark:text-app-text">
                Availability Calendar
              </div>
              <div className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary">
                Filter by date range to see which days and times are available.
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setNextDays(7)}
                className="px-3 py-1.5 rounded-full bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text text-[11px]"
              >
                Next 7 days
              </button>
              <button
                type="button"
                onClick={() => setNextDays(30)}
                className="px-3 py-1.5 rounded-full bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text text-[11px]"
              >
                Next 30 days
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
            <div>
              <label className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">From</label>
              <input
                type="date"
                className="mt-1 w-full px-2 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">To</label>
              <input
                type="date"
                className="mt-1 w-full px-2 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            <button
              type="button"
              onClick={fetchSlots}
              className="px-3 py-2 rounded-xl bg-app-text-light dark:bg-app-text text-app-bg-light dark:text-app-bg font-semibold text-[11px]"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={() => {
                setFrom('');
                setTo('');
                fetchSlots();
              }}
              className="px-3 py-2 rounded-xl bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text font-semibold text-[11px]"
            >
              Clear filters
            </button>
          </div>

          {/* Clear slots range */}
          <div className="mt-3 border-t border-app-border-light dark:border-app-border pt-3 space-y-2">
            <div className="text-sm font-semibold text-app-text-light dark:text-app-text">
              Clear slots in date range
            </div>
            <div className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary">
              Remove generated slots if you need to reset your calendar. You can
              keep slots that already have appointments.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[repeat(3,minmax(0,1fr))] gap-2 items-end">
              <div>
                <label className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">From</label>
                <input
                  type="date"
                  className="mt-1 w-full px-2 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border"
                  value={clearFrom}
                  onChange={(e) => setClearFrom(e.target.value)}
                />
              </div>
              <div>
                <label className="text-app-text-secondary-light dark:text-app-text-secondary text-[11px]">To</label>
                <input
                  type="date"
                  className="mt-1 w-full px-2 py-2 rounded-lg bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border"
                  value={clearTo}
                  onChange={(e) => setClearTo(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="flex items-center gap-2 text-[11px] text-app-text-light dark:text-app-text">
                  <input
                    type="checkbox"
                    checked={onlyEmpty}
                    onChange={(e) => setOnlyEmpty(e.target.checked)}
                  />
                  Only remove slots with no appointments
                </label>
                <button
                  type="button"
                  disabled={clearing}
                  onClick={clearRange}
                  className={`mt-1 w-full px-3 py-2 rounded-xl font-semibold text-[11px] ${
                    clearing
                      ? 'bg-app-bg-light dark:bg-app-bg text-app-text-secondary-light dark:text-app-text-secondary'
                      : 'bg-red-600/80 text-black'
                  }`}
                >
                  {clearing ? 'Clearing…' : 'Clear slots'}
                </button>
              </div>
            </div>
          </div>

          {/* Slots list */}
          {loadingSlots ? (
            <div className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary mt-2">
              Loading slots…
            </div>
          ) : groupedSlots.length === 0 ? (
            <div className="text-[11px] text-app-text-secondary-light dark:text-app-text-secondary mt-2">
              No slots found for this range.
            </div>
          ) : (
            <div className="space-y-3 max-h-80 overflow-y-auto pr-1 mt-2">
              {groupedSlots.map(([dayLabel, items]) => (
                <div
                  key={dayLabel}
                  className="bg-app-bg-light dark:bg-app-bg border border-app-border-light dark:border-app-border rounded-xl p-2"
                >
                  <div className="text-[11px] font-semibold text-app-text-light dark:text-app-text mb-1">
                    {new Date(dayLabel).toLocaleDateString()}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {items.map((s) => {
                      const remaining =
                        (s.capacity ?? 1) - (s.bookedCount ?? 0);
                      const isFull = remaining <= 0;
                      return (
                        <div
                          key={s._id}
                          className={`px-2 py-1 rounded-lg border text-[11px] flex items-center gap-2 ${
                            isFull
                              ? 'border-app-input-border-light dark:border-app-input-border bg-app-bg-light/40 dark:bg-app-bg/40 text-app-text-secondary-light dark:text-app-text-secondary'
                              : 'border-app-input-border-light dark:border-app-input-border bg-app-bg-light dark:bg-app-bg text-app-text-light dark:text-app-text'
                          }`}
                        >
                          <span>{s.time}</span>
                          <span className="text-app-text-secondary-light dark:text-app-text-secondary">
                            • {remaining} left
                          </span>
                          <button
                            type="button"
                            onClick={() => removeSlot(s._id)}
                            className="text-[10px] px-2 py-0.5 rounded-md bg-red-600/80 text-black"
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DealerSlotsScreen;
